export type SortDirection = 'asc' | 'desc';

// `numeric: true` makes digit runs compare by value rather than character-by-character,
// so "Spot 2" sorts before "Spot 10" instead of after it.
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

/**
 * Compares two display names alphabetically, treating embedded digit runs as numbers, and
 * honoring the requested sort direction.
 *
 * @param a - First name.
 * @param b - Second name.
 * @param direction - `'asc'` (default) or `'desc'`.
 * @returns Negative, zero, or positive, per the standard `Array#sort` comparator contract.
 */
export const compareNames = (
  a: string,
  b: string,
  direction: SortDirection = 'asc',
): number => {
  const result = collator.compare(a, b);
  return direction === 'asc' ? result : -result;
};

/**
 * Returns a new array of items sorted by their display name via {@link compareNames}.
 *
 * @param items - Items to sort; the source array is left untouched.
 * @param getName - Resolves the display name to sort by for a given item.
 * @param direction - `'asc'` (default) or `'desc'`.
 * @returns A new, sorted array.
 */
export const sortByName = <T>(
  items: T[],
  getName: (item: T) => string,
  direction: SortDirection = 'asc',
): T[] =>
  [...items].sort((a, b) => compareNames(getName(a), getName(b), direction));
